
import { Component, Vue, Watch, Prop, Inject } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

// @ts-ignore
import QRCode from "qrcode"

import moment from "moment"
import { LayoutLocations } from "@/types/templateLayout"
import CaseLinks from "./CaseLinks.vue"
import CaseUsers from "@/components/CaseDetails/CaseUsers.vue"
import Invite from "@/components/CaseDetails/Users/Invite.vue"
import CaseActions from "@/components/CaseDetails/CaseActions.vue"
import CaseFields from "@/components/CaseDetails/CaseFields.vue"
import CaseMergeModal from "@/components/CaseDetails/CaseMergeModal.vue"
import Tags from "@/components/Fields/Tags.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"
import CCEventModal from "@/components/CaseDetails/CCEvent/CCEventModal.vue"
import UserImage from "@/components/UserImage.vue"

import CaseFile from "@/types/caseFile"

import { Field, Area, Template, CaseUser, CaseField, Status, Severity, TemplateDetails, CaseDetails } from "@/types"
import { CaseEvent, CaseEvents } from "@/types/originatingEvents"
import User from "@/types/user"

import api from "@/services/api"
import SkeletonLoader from "../SkeletonLoader.vue"
import { BModal } from "bootstrap-vue"

import { IntegrationOutOption } from "@/types/integration"
import { UserRole, UserRoleDefinition } from "@/types/caseUser"
import { TenantFeatures } from "@/types/tenantFeatures"

import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"
import { SourceType } from "@/types/sourceType"
import { CadenceInstance } from "@/types/cadence"
import { LabeledValue } from "@/types/labeledValue"
import { cloneDeep } from "lodash"
import { formatFieldsMixin } from "@/mixins/formatFieldsMixin"
import { getIsCadenceVisibleOnCase, getCanUserDisableCadence, normalizeCaseCadenceProperties } from "@/scripts/caseDetails"

const caseStore = namespace("cases")

@Component({
	components: {
		"user-image": UserImage,
		"case-users": CaseUsers,
		"scrollbar": VuePerfectScrollbar,
		"tags": Tags,
		"v-select": vSelect,
		"invite-user": Invite,
		"case-links": CaseLinks,
		"case-actions": CaseActions,
		"area-select": AreaSelect,
		"case-fields": CaseFields,
		"loader": SkeletonLoader,
		"case-merge-modal": CaseMergeModal
	}
})
export default class CaseSideBar extends Vue {
	$refs!: {
		confirmCloseModal: BModal
		caseMergeModal: CaseMergeModal,
	}

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	@caseStore.Getter("getStatuses") public statuses!: Status[];
	@caseStore.Getter("getSeverities") public severities!: Severity[];
	@caseStore.State("areas") public areas!: Area[];

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents | null;

	@caseStore.Action public caseOpen!: (caseId: string) => void;

	@State
	public me!: User;

	@caseStore.Mutation
	public setShowCCEvent!: (ccEvent: CaseEvent) => void;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	/**
	 * Returns if a feature is enabled on the current tenancy
	 */
	@Inject({ default: () => () => false }) featureEnabled!: (feature: TenantFeatures) => boolean

	@Prop({ default: false })
	canEditCurrentCase!: boolean;

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public template!: TemplateDetails

	@Prop()
	public caseFiles!: CaseFile[];

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: false })
	editAfterClosePermissions!: boolean

	@Prop({ default: false })
	canMakeClosedChanges!: boolean

	@Prop({ default: () => [] })
	caseIntegrationLinks!: IntegrationOutOption[]

	@Prop({ default: null })
	caseCadenceInstance!: CadenceInstance | null

	formatFieldsMixin = formatFieldsMixin

	LayoutLocations = LayoutLocations

	public get anyUserRoles () {
		if (!this.currentCase || !this.currentCase.fields) return false
		return Object.keys(this.currentCase.fields)
			.some(fieldId => this.currentCase.fields[fieldId].field.dataType === "user-role")
	}

	public get qrImage () {
		let data: string | undefined

		QRCode.toDataURL(location.href, { width: 300 }, (err: Error | null, dataUrl: string | undefined) => {
			if (err) {
				console.error("Error generating QR code:", err)
				return null
			}

			data = dataUrl
		})

		return data
	}

	private get caseFieldsNotValidated (): CaseField[] {
		if (!this.currentCase || !this.currentCase.fields) {
			return []
		}

		let allFields: CaseField[] = []
		if (this.currentCase && this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (this.currentCase) {
					allFields.push(this.currentCase.fields![fieldId])
				}
			})
		}

		const outstandingFields = allFields.filter(field => this.fieldRequired(field))

		return outstandingFields
	}

	/**
	 * @returns false if the field is valid, true if field is inaccessible, not required, or has a value set
	 */
	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.caseFiles.filter(caseFile => caseFile.fieldId === id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	/**
	 * Checks that all required actions are completed
	 * @returns If case is null or actions is null or all actions set as Required on current case are completed
	 */
	private get caseActionsValidated (): boolean {
		if (!this.currentCase || !this.currentCase.actions) {
			return true
		}

		const isCase: boolean = this.currentCase.isCase
		return !this.currentCase.actions.some(action => ((action.requiredRecord && isCase === false) ||
															(action.requiredCase && isCase)) &&
															!action.completedBy)
	}

	private get integrationOptionLinks (): Set<string> {
		return new Set<string>(this.caseIntegrationLinks
			? this.caseIntegrationLinks.map(link => link.integrationOut)
			: [])
	}

	private onCaseChanged () {
		if (this.currentCase.id) {
			normalizeCaseCadenceProperties(this.currentCase, this.template, this.getIsFeatureEnabled)
			api.saveCase(this.currentCase)
		}
	}

	public openEvent (event: CaseEvent) {
		this.setShowCCEvent(event)
	}

	public updateLocationsList () {
		if (this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	public makeCase () {
		api.recordToCase(this.currentCase.id!)
	}

	/**
	 * If user role location is enabled, returns concatenated string listing users that do have location not set
	 * If feature is not set, this will return null.
	 */
	public get userRoleLocationsEmpty (): string | null {
		if (!this.currentCase || !this.currentCase.fields) return null
		if (!this.featureEnabled(TenantFeatures.UserRoleLocation)) return null

		const outstandingUserRoleLocations =
			Object.keys(this.currentCase.fields) // For each field ID
				.map(fieldId => this.currentCase!.fields![fieldId]) // Pull field
				.filter(field => { // Find only user role fields with a value
					return field.field.dataType === "user-role" &&
						field.value != null &&
						field.field.extensions
				})
				.map(field => { // Convert each field to a list of users in which location is not set
					const value = field.value as UserRole
					const userRole = field.field.extensions as UserRoleDefinition

					if (userRole.subroles && userRole.subroles.length > 0) {
						return value.subRoles
							? value.subRoles.map(subRoleValue => {
								if (!subRoleValue.users || subRoleValue.users.length === 0) return []
								const subRole = userRole.subroles!.find(sr => sr.title === subRoleValue.title)
								if (!subRole || !subRole.allowTimeTracking || !subRole.allowLocationTracking) return []

								return this.locationMissingUsers(subRoleValue.users, field.field.title)
							}).flat()
							: []
					} else if (userRole.allowTimeTracking && userRole.allowLocationTracking) {
						return this.locationMissingUsers(value.users, field.field.title)
					}

					return []
				}).flat() // Smoosh list of lists down to one list
		if (outstandingUserRoleLocations.length === 0) return null // No-one missing location

		// Turn to string
		return "Location missing for " + outstandingUserRoleLocations.map(l => `${l.user} (${l.field})`).join(", ")
	}

	public get closeCaseButtonToolTipTitle (): string {
		if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0 && this.caseActionsValidated === false) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " and actions to be completed."
		} else if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " to be completed."
		} else if (this.caseActionsValidated === false) {
			return "All field actions required"
		} else if (this.userRoleLocationsEmpty) {
			return this.userRoleLocationsEmpty
		}

		return ""
	}

	public get closeConfirmTitle () {
		return this.currentCase.isCase
			? "Close " + this.getCaseName
			: "Close " + this.getRecordName
	}

	public get caseCreatedLocalTime (): string {
		return this.currentCase.created ? moment(this.currentCase.created).format("l LTS") : ""
	}
	public get caseClosedLocalTime (): string {
		return this.currentCase.closed ? moment(this.currentCase.closed).format("l LTS") : ""
	}

	/**
	 * Checks if something is preventing the case from being closed
	 * @return true if all case fields validated, all required actions completed and no user roles require location, false otherwise
	 */
	public get canClose (): boolean {
		return (!this.caseFieldsNotValidated || this.caseFieldsNotValidated.length === 0) && this.caseActionsValidated && !this.userRoleLocationsEmpty
	}

	private confirmCloseCase (id: string): void {
		if (!this.canClose) {
			return
		}

		this.$refs.confirmCloseModal.show()
	}

	private cancelCloseCase (id: string): void {
		this.$emit("cancelCloseCase")
	}

	public makeClosedChanges () {
		this.$emit("makeClosedChanges")
	}

	private showMarkSensitiveConfirmation () {
		if (this.currentCase.id) {
			this.markSensitiveModalShown = true
		}
	}

	private markSensitiveModalShown = false

	private async markSensitive () {
		if (this.currentCase.id) {
			try {
				await api.markAsSensitive(this.currentCase.id)
				await this.caseOpen(this.currentCase.caseNumber!)
			} catch (err) {
				console.log(err)
			}
		}
		this.markSensitiveModalShown = false
	}

	private async GenerateQRCode () {
		this.$bvModal.show("QR-Modal")
	}

	private mergeRecords () {
		this.$refs.caseMergeModal.open()
	}

	public getIsCadenceVisible (): boolean {
		return getIsCadenceVisibleOnCase(this.currentCase, this.template, this.getIsFeatureEnabled)
	}

	public showDisableCadence (): boolean {
		return getCanUserDisableCadence(this.currentCase, this.me, this.template, this.getIsFeatureEnabled)
	}

	public get showSourceTypeBadge (): boolean {
		return this.featureEnabled(TenantFeatures.SourceType) && !!this.currentCase.sourceType
	}

	public doCaseClose (closeCaseId: string) {
		if (this.currentCase.id === closeCaseId) {
			this.$emit("close", this.currentCase)
		}
	}

	public async hideFromInsights () {
		if (!this.currentCase.id) {
			return
		}

		this.$set(this.currentCase, "hiddenFromInsights", true)
		await api.saveCase(this.currentCase)
		this.hideFromInsightsModalShown = false
	}

	public async unhideFromInsights () {
		if (!this.currentCase.id) {
			return
		}
		this.$set(this.currentCase, "hiddenFromInsights", false)
		await api.saveCase(this.currentCase)
	}

	/** Shows the "hide from insights" confirm modal. */
	private hideFromInsightsModalShown = false
	private showHideFromInsightsConfirmation () {
		this.hideFromInsightsModalShown = true
	}

	/** Shows the delete confirm modal. */
	private deleteCaseModalShown = false
	private showDeleteCaseModal () {
		this.deleteCaseModalShown = true
	}

	/** Delete confirmation text. The user is require to type the name of the case in order to delete. */
	private deleteConfirmText = ""

	/** Deletes the current case. */
	public async deleteCase () {
		if (this.currentCase.id &&
			((this.currentCase.title && this.deleteConfirmText === this.currentCase.title) ||
			(!this.currentCase.title && this.deleteConfirmText === "delete"))
		) {
			await api.deleteCase(this.currentCase.id!)
			window.location.reload()
		}
	}

	public pushTo (outputOption: IntegrationOutOption) {
		this.$emit("integrationPush", outputOption)
	}

	private caseOrRecordText () : string {
		return this.currentCase.isCase ? "Case" : "Record"
	}

	/**
	 * Pulls users missing locations from user role field user list
	 * @param users user list from either top level or a subrole (or null)
	 * @param fieldtitle Title from user role field
	 */
	private locationMissingUsers (users: CaseUser[] | null, fieldTitle: string): { field: string, user: string }[] {
		return users ? users
			.filter((u: CaseUser) => u.location === null || u.location === undefined)
			.map((u: CaseUser) => {
				return { field: fieldTitle, user: u.name }
			})
			: []
	}

	get cadenceRunningTotalsFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.CadenceRunningTotals)
	}

	get userRoleTimeAsHoursAndMinutesFeatureIsEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.UserRoleTimeAsHoursMinutes)
	}

	/**
	 * Returns the running total sections for the current cadence instance (if applicable) with their fields and values.
	 * Returned as LabeledValue<sectionName, LabeledValue<fieldName, fieldValue>[]>[]
	 */
	get cadenceRunningTotalSections (): LabeledValue[] {
		if (!this.cadenceRunningTotalsFeatureIsEnabled) return []

		if (!this.caseCadenceInstance?.runningTotalValues) return []

		const rtSections = this.template.cadenceDetails?.runningTotalSectionFields ?? null
		if (!rtSections) return []

		const rtSectionNames = Object.keys(rtSections)

		let filledSections: LabeledValue[] = []

		for (const sectionName of rtSectionNames) {
			const sectionFieldIds = rtSections[sectionName]
			const sectionValues = sectionFieldIds.map((fieldId: string) => {
				const templateField = this.template.fields.firstOrDefault(field => field.field.id === fieldId)
				if (!templateField) return null

				if (!(templateField.field.id! in this.caseCadenceInstance!.runningTotalValues!)) {
					return null
				}

				const rtValue = cloneDeep(this.caseCadenceInstance!.runningTotalValues![templateField.field.id!])
				rtValue.value = parseFloat(Number(rtValue.value).toFixed(2)).toString()

				if (templateField.field.dataType === "user-role") {
					if (this.userRoleTimeAsHoursAndMinutesFeatureIsEnabled) {
						// convert rtValue from hours to hours and minutes
						rtValue.value = this.formatFieldsMixin.methods.timeInHoursAndMinutes(rtValue.value)
					} else {
						// display decimal hours
						rtValue.value = rtValue.value + " hours"
					}
				}

				return {
					label: templateField.field.title,
					value: rtValue
				}
			}).filter((v: any) => v !== null)

			if (sectionValues.length > 0) {
				filledSections.push({
					label: sectionName,
					value: sectionValues
				})
			}
		}

		return filledSections
	}
}

